import { usePlatformDetection } from "@/hooks/usePlatformDetection";

export interface PhotoDownloadOptions {
  photo: {
    id: string;
    url: string;
    filename?: string;
  };
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Mobile-specific photo download utility that handles different approaches
 * for iOS and Android devices to save photos to the Photo Library
 */
export const useMobilePhotoDownload = () => {
  const { platform, isMobile } = usePlatformDetection();

  const downloadPhoto = async ({ photo, onSuccess, onError }: PhotoDownloadOptions) => {
    try {
      if (!isMobile) {
        // Desktop behavior - use standard download
        await downloadForDesktop(photo);
        onSuccess?.();
        return;
      }

      // Mobile behavior - different approaches for iOS and Android
      if (platform === "ios") {
        await downloadForIOS(photo);
        onSuccess?.();
      } else if (platform === "android") {
        await downloadForAndroid(photo);
        onSuccess?.();
      } else {
        // Fallback for other mobile platforms
        await downloadForMobileFallback(photo);
        onSuccess?.();
      }
    } catch (error) {
      console.error("Photo download failed:", error);
      onError?.(error instanceof Error ? error.message : "Download failed");
    }
  };

  return { downloadPhoto, isMobile, platform };
};

/**
 * Desktop download implementation using standard <a> tag approach
 */
const downloadForDesktop = async (photo: { url: string; filename?: string; id: string }) => {
  const response = await fetch(photo.url);
  if (!response.ok) {
    throw new Error("Failed to fetch image");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = photo.filename || `photo-${photo.id}.jpg`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * iOS-specific download implementation
 * Uses a synchronous approach that works with Safari's popup blocking
 * Creates a direct download link that maintains user gesture context
 *
 * Safari popup blocking issue:
 * - Safari blocks window.open() calls that occur after async operations
 * - This includes image loading, canvas processing, etc.
 * - Solution: Use direct link approach that maintains user gesture context
 */
const downloadForIOS = async (photo: { url: string; filename?: string }) => {
  try {
    // For iOS Safari, we need to work within the user gesture context
    // We'll create a direct link to the image URL and let Safari handle it
    const link = document.createElement("a");
    link.href = photo.url;
    link.target = "_blank";
    link.download = photo.filename || "photo.jpg";

    // Add the link to the DOM temporarily and trigger click
    link.style.display = "none";
    document.body.appendChild(link);

    // Trigger the click immediately to maintain user gesture context
    link.click();

    // Clean up
    document.body.removeChild(link);

    // Note: This approach may not work for cross-origin images
    // If the image is cross-origin, we'll need to handle it differently
  } catch (error) {
    // If direct link fails, try the canvas approach as fallback
    await downloadForIOSCanvas(photo);
  }
};

/**
 * Fallback iOS implementation using canvas for cross-origin images
 * This is used when direct linking fails
 */
const downloadForIOSCanvas = async (photo: { url: string; filename?: string }) => {
  return new Promise<void>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Could not create canvas context"));
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        // Convert to data URL
        const dataUrl = canvas.toDataURL("image/jpeg", 0.9);

        // Create a temporary link element
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = photo.filename || "photo.jpg";

        // Add the link to the DOM temporarily and trigger click
        link.style.display = "none";
        document.body.appendChild(link);

        // Trigger the click immediately
        link.click();

        // Clean up
        document.body.removeChild(link);

        resolve();
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    img.src = photo.url;
  });
};

/**
 * Android-specific download implementation
 * Uses a combination of approaches for better compatibility
 */
const downloadForAndroid = async (photo: { url: string; filename?: string; id: string }) => {
  try {
    // First try the standard download approach
    const response = await fetch(photo.url);
    if (!response.ok) {
      throw new Error("Failed to fetch image");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // Create a temporary link
    const link = document.createElement("a");
    link.href = url;
    link.download = photo.filename || `photo-${photo.id}.jpg`;
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
  } catch (error) {
    // Fallback to iOS approach if standard download fails
    await downloadForIOS(photo);
  }
};

/**
 * Fallback implementation for other mobile platforms
 */
const downloadForMobileFallback = async (photo: { url: string; filename?: string }) => {
  // Use the iOS approach as it's more universally compatible
  await downloadForIOS(photo);
};
